import {AttrsSchema, Component} from 'panel';

// @ts-check
import {apiClient} from 'common/util/api-client';
import {ItemClickEvent, SectionItem} from 'common/components/items-menu/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {ErrorResponse} from 'common/util/shared-client';
import {TagItem} from 'common/components/tag-input/tag-item';
import {TagInputChangeEvent} from 'common/components/tag-input/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {AuthorizeStatus, openSlackOauthBroadcastChannel} from 'common/util/slack';

import {template} from './template';
import css from './index.inline.styl';
import {SelectedChannelsChangedEvent, SlackAuthProblem, SlackChannel, SlackChannelsList} from './types';

const MIN_INPUT_SIZE = `2`;

export interface SlackChannelSelectorState {
  loadingChannels: boolean;
  showingChannelList: boolean;
  channelChoices: SlackChannel[];
  slackTeamId: string;
  selectedChannels: Map<string, SlackChannel>;
  inputText: string;
  inputSize: string;
  slackAuthProblem: string;
  slackOAuthLoopResult?: AuthorizeStatus;
}

export interface SlackChannelSelectorJsxProps {
  attrs?: {
    'selected-channels': SlackChannel[];
    'is-readonly': boolean;
  };
}

const enum ElmRefs {
  TagInput = `tagInput`,
}

export class SlackChannelSelector extends Component<
  SlackChannelSelectorState,
  SlackChannelSelectorJsxProps['attrs'],
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  private _slackAuthBroadcastChannel: BroadcastChannel;
  static get attrsSchema(): AttrsSchema<SlackChannelSelectorJsxProps['attrs']> {
    return {
      'selected-channels': `json`,
      'is-readonly': `boolean`,
      ...super.attrsSchema,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        loadingChannels: false,
        showingChannelList: false,
        channelChoices: [],
        slackTeamId: null,
        selectedChannels: new Map<string, SlackChannel>(),
        inputText: ``,
        inputSize: MIN_INPUT_SIZE,
        slackAuthProblem: null,
        slackOAuthLoopResult: null,
      },
    };
  }

  selectChannel(channel: SlackChannel) {
    const {selectedChannels} = this.state;
    selectedChannels.set(channel.id, channel);
    this.update({selectedChannels, inputText: ``});
    this.dispatchSelectedChannelsChanged();
  }

  deselectChannel(channel: SlackChannel) {
    this.deselectChannelById(channel.id);
  }

  deselectChannelById(channelId: string) {
    const selectedChannels = this.state.selectedChannels;
    selectedChannels.delete(channelId);
    this.update({selectedChannels, inputText: ``});
    this.dispatchSelectedChannelsChanged();
  }

  dispatchSelectedChannelsChanged() {
    const event: SelectedChannelsChangedEvent = new CustomEvent(`selectedChannelsChanged`, {
      detail: {selectedChannels: Array.from(this.state.selectedChannels.values()), teamId: this.state.slackTeamId},
    });
    this.dispatchEvent(event);
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute(`tabindex`, `0`);
    this.addEventListener(`blur`, this.handleBlur);
    this.addEventListener(`focus`, this.handleFocus);
    this._slackAuthBroadcastChannel = openSlackOauthBroadcastChannel();
    this._slackAuthBroadcastChannel.onmessage = (event: MessageEvent<{authStatus: AuthorizeStatus}>) => {
      this.handleSlackOauthBroadcast(event);
    };
    this.fetchSlackChannels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(`blur`, this.handleBlur);
    this.removeEventListener(`focus`, this.handleFocus);
    this.hideChannelList();
    this._slackAuthBroadcastChannel?.close();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `selected-channels`) {
      const selectedChannels = new Map<string, SlackChannel>(
        (this.attr(`selected-channels`) || []).map((channel) => [channel.id, channel]),
      );
      this.update({selectedChannels});
    }
  }

  handleSlackOauthBroadcast(event: MessageEvent<{authStatus: AuthorizeStatus}>) {
    const {authStatus} = event.data;
    this.update({slackOAuthLoopResult: authStatus});
  }

  async fetchSlackChannels() {
    if (this.state.channelChoices.length || this.state.loadingChannels) {
      return;
    }

    try {
      this.update({loadingChannels: true, slackAuthProblem: null});
      const response = await apiClient.get<SlackChannelsList>(`/api/app/slack/channels`);
      if (response) {
        this.update({
          channelChoices: response.channels,
          slackTeamId: response.team_id,
          loadingChannels: false,
        });
      } else {
        this.update({loadingChannels: false});
      }
    } catch (error) {
      const details = error.response as ErrorResponse;
      let slackAuthProblem = null;
      if (details.type in SlackAuthProblem) {
        slackAuthProblem = details.type;
      }
      this.update({loadingChannels: false, slackAuthProblem});
    }
  }

  private informUserOfSlackAuthResult() {
    const success = this.state.slackOAuthLoopResult === AuthorizeStatus.Success;
    addToast({
      message: success
        ? `The Slack app has successfully updated`
        : `The Slack App could not be updated. Please try again soon.`,
      type: success ? ToastType.SUCCESS : ToastType.WARNING,
    });
  }

  handleFocus() {
    if (!this.state.slackAuthProblem) {
      this.loadAndShowChannelList();
    }
    if (this.state.slackOAuthLoopResult) {
      this.informUserOfSlackAuthResult();
      this.update({slackOAuthLoopResult: null});
    }
  }

  handleBlur() {
    this.hideChannelList();
  }

  setFocusOnInput() {
    this.elmRefs.tagInput.focus();
  }

  loadAndShowChannelList() {
    this.fetchSlackChannels();
    if (!this.state.showingChannelList) {
      this.update({showingChannelList: true});
    }
  }

  hideChannelList() {
    this.update({showingChannelList: false});
  }

  elmRefs: Record<ElmRefs, HTMLElement | null> = {
    [ElmRefs.TagInput]: null,
  };

  setElmRef(key: string, elm: HTMLElement) {
    this.elmRefs[key] = elm;
  }

  getSlackChannelsForItemsMenu = () => {
    const selectedChannelIds = [...this.state.selectedChannels].map((channel) => channel[0]);
    return this.state.channelChoices.map(
      (channel): SectionItem<SlackChannel> => {
        return {value: channel, label: channel.name, isSelected: selectedChannelIds.includes(channel.id)};
      },
    );
  };

  onInputWrapperClick = () => {
    this.loadAndShowChannelList();
    this.setFocusOnInput();
  };

  onSearchInputButtonPress = () => {
    if (this.state.showingChannelList) {
      this.hideChannelList();
    } else {
      this.loadAndShowChannelList();
    }
    this.setFocusOnInput();
  };

  onChannelListItemClick = (ev: ItemClickEvent<SlackChannel>) => {
    const item = ev.detail.item;
    const channel = item.value;
    if (item.isSelected) {
      // going from selected=>not
      this.deselectChannel(channel);
    } else {
      this.selectChannel(channel);
    }
  };

  handleReauthorizeClick = () => {
    // so when the user comes back to this window we retry
    this.update({
      slackAuthProblem: null,
    });
  };

  picker = {
    getChannelsAsTags: (): TagItem[] => {
      return Array.from(this.state.selectedChannels).map<TagItem>(([channelId, channel]) => {
        return {
          label: channel.name,
          value: channelId,
        };
      });
    },

    handleTagInputChange: (ev: TagInputChangeEvent) => {
      const {detail} = ev;
      if (detail.action === `text`) {
        this.update({inputText: detail.value});
      } else if (detail.action === `removeTag`) {
        this.deselectChannelById(detail.tag.value);
      }
    },
    handleTagInputFocus: () => {
      this.loadAndShowChannelList();
    },

    handleMpTagInputInsert: ({elm}) => {
      this.setElmRef(ElmRefs.TagInput, elm);
    },
  };
}

export const SlackChannelSelectorEl = defineAndMakeJsxFactory(`mp-slack-channel-selector`, SlackChannelSelector);
export const SlackChannelSelectorReact = panel2react(`mp-slack-channel-selector`, SlackChannelSelector);
